import React, { useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Grid,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Tabs,
    Tab,
    Stepper,
    Step,
    StepLabel,
    StepContent
} from '@mui/material';
import { AppResponsiveContainer } from 'components/containers/AppResponsiveContainer';
import { AppButton } from 'components/widgets/AppButton';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
// import BuildIcon from '@mui/icons-material/Build';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SpeedIcon from '@mui/icons-material/Speed';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import EngineeringIcon from '@mui/icons-material/Engineering';
import { useApp } from 'AppProvider';
import ContactUsPage from '../ContactUsPage';

const SafetyGuidePage = () => {
    const [activeTab, setActiveTab] = useState(0);
    const { navigateToPage } = useApp();

    const safetyFeatures = [
        {
            icon: <SecurityIcon fontSize="large" />,
            title: 'Safety Standards',
            description: 'Adherence to international safety standards and protocols',
        },
        // {
        //     icon: <BuildIcon fontSize="large" />,
        //     title: 'Equipment Inspection',
        //     description: 'Regular maintenance and inspection of transport equipment',
        // },
        {
            icon: <EngineeringIcon fontSize="large" />,
            title: 'Trained Personnel',
            description: 'Certified drivers and loading specialists',
        }
    ];

    const loadingSteps = [
        {
            label: 'Pre-Loading Inspection',
            description: [
                'Check chassis condition and structural integrity',
                'Verify all securing points are functional',
                'Inspect loading equipment and ramps',
                'Document any existing damage',
            ],
        },
        {
            label: 'Loading Procedure',
            description: [
                'Position transport vehicle on level ground',
                'Set proper ramp angle for safe loading',
                'Maintain minimum 3-point contact',
                'Follow proper weight distribution guidelines',
            ],
        },
        {
            label: 'Securing the Load',
            description: [
                'Use appropriate tie-down points',
                'Apply correct tension to securing straps',
                'Double-check all connection points',
                'Add safety markers and reflectors',
            ],
        },
        {
            label: 'Final Checks',
            description: [
                'Verify load stability and balance',
                'Check clearance heights and widths',
                'Confirm all documentation is complete',
                'Perform walk-around inspection',
            ],
        },
    ];

    const safetyChecklists = {
        vehicleChecks: [
            'Brake system functionality',
            'Tire pressure and condition',
            'Securing equipment integrity',
            'Warning systems operation',
            'Emergency equipment presence',
            'Lighting system checks',
        ],
        driverGuidelines: [
            'Valid commercial driving license',
            'Mandatory rest periods compliance',
            'Weather condition assessment',
            'Route planning and risk assessment',
            'Emergency protocol knowledge',
            'Communication system checks',
        ],
        emergencyProtocols: [
            'Immediate incident reporting',
            'Emergency contact procedures',
            'First aid kit accessibility',
            'Fire extinguisher locations',
            'Breakdown safety measures',
            'Spill control procedures',
        ]
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    return (
        <Box>
            {/* Hero Section */}
            <Box sx={{ 
                bgcolor: 'primary.main', 
                color: 'primary.contrastText', 
                py: 6,
                position: 'relative',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                }
            }}>
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography variant="h2" gutterBottom>
                        Safety Guidelines
                    </Typography>
                    <Typography variant="h5">
                        Ensuring Safe and Secure Transport of Your Valuable Assets
                    </Typography>
                </Container>
            </Box>

            {/* Safety Features Section */}
            <Container maxWidth="lg" sx={{ py: 8 }}>
                <Typography variant="h4" align="center" gutterBottom>
                    Our Safety Commitments
                </Typography>
                <AppResponsiveContainer spacing={4}>
                    {safetyFeatures.map((feature, index) => (
                        <Card key={index} sx={{ height: '100%' }}>
                            <CardContent sx={{ 
                                textAlign: 'center',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                p: 3
                            }}>
                                <Box color="primary.main" mb={2} sx={{ transform: 'scale(1.5)' }}>
                                    {feature.icon}
                                </Box>
                                <Typography variant="h6" gutterBottom>
                                    {feature.title}
                                </Typography>
                                <Typography color="text.secondary">
                                    {feature.description}
                                </Typography>
                            </CardContent>
                        </Card>
                    ))}
                </AppResponsiveContainer>
            </Container>

            {/* Loading Procedure Section */}
            <Box sx={{ bgcolor: 'grey.100', py: 8 }}>
                <Container maxWidth="md">
                    <Typography variant="h4" align="center" gutterBottom>
                        Loading and Securing Procedures
                    </Typography>
                    <Stepper orientation="vertical" sx={{ mt: 4 }}>
                        {loadingSteps.map((step, index) => (
                            <Step key={index} active={true}>
                                <StepLabel>
                                    <Typography variant="h6">{step.label}</Typography>
                                </StepLabel>
                                <StepContent>
                                    <List>
                                        {step.description.map((item, idx) => (
                                            <ListItem key={idx}>
                                                <ListItemIcon>
                                                    <CheckCircleIcon color="primary" />
                                                </ListItemIcon>
                                                <ListItemText primary={item} />
                                            </ListItem>
                                        ))}
                                    </List>
                                </StepContent>
                            </Step>
                        ))}
                    </Stepper>
                </Container>
            </Box>

            {/* Safety Checklists Section */}
            <Container maxWidth="lg" sx={{ py: 8 }}>
                <Typography variant="h4" align="center" gutterBottom>
                    Safety Checklists
                </Typography>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs value={activeTab} onChange={handleTabChange} centered>
                        <Tab label="Vehicle Checks" />
                        <Tab label="Driver Guidelines" />
                        <Tab label="Emergency Protocols" />
                    </Tabs>
                </Box>
                <Box role="tabpanel" hidden={activeTab !== 0}>
                    <Grid container spacing={2}>
                        {safetyChecklists.vehicleChecks.map((item, index) => (
                            <Grid item xs={12} sm={6} key={index}>
                                <ListItem>
                                    <ListItemIcon>
                                        <DirectionsCarIcon color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary={item} />
                                </ListItem>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
                <Box role="tabpanel" hidden={activeTab !== 1}>
                    <Grid container spacing={2}>
                        {safetyChecklists.driverGuidelines.map((item, index) => (
                            <Grid item xs={12} sm={6} key={index}>
                                <ListItem>
                                    <ListItemIcon>
                                        <SpeedIcon color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary={item} />
                                </ListItem>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
                <Box role="tabpanel" hidden={activeTab !== 2}>
                    <Grid container spacing={2}>
                        {safetyChecklists.emergencyProtocols.map((item, index) => (
                            <Grid item xs={12} sm={6} key={index}>
                                <ListItem>
                                    <ListItemIcon>
                                        <WarningIcon color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary={item} />
                                </ListItem>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </Container>

            {/* Emergency Contact Section */}
            <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 6 }}>
                <Container maxWidth="md">
                    <Card sx={{ bgcolor: 'primary.dark' }}>
                        <CardContent sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="h5" gutterBottom>
                                24/7 Safety Support
                            </Typography>
                            <Typography paragraph>
                                Our safety team is available round the clock to assist you
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                                <AppButton
                                    variant="contained"
                                    color="secondary"
                                    size="large"
                                    onClick={() => navigateToPage(<ContactUsPage />)}
                                    startIcon={<LocalShippingIcon />}
                                >
                                    Emergency Helpline
                                </AppButton>
                            </Box>
                        </CardContent>
                    </Card>
                </Container>
            </Box>
        </Box>
    );
};

export default SafetyGuidePage;